import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  ProDocItem,
  ProDocReply,
  GetProDocRepliesResponse,
  AddProDocReplyRequest,
  AddProDocReplyResponse,
} from '@dreamai/types';

import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { ReactComponent as CommentIcon } from '../../icons/speech-bubble.svg';
import SidewaysLoading from './sidebar/SidewaysLoading';
import ReplyItem from './ReplyItem';
import ReplyForm from './ReplyForm';
import CommentCountButton from './CommentCountButton';
import { useAgent } from '../../contexts/AgentContext';

import './pro-doc-replies.scss';
import ErrorState from 'components/common/ErrorState';

interface ProDocItemWithRepliesProps {
  item: ProDocItem;
  children: React.ReactNode;
  inComponentView?: boolean;
  agentPredominantColour?: string;
  onCommentClick?: (itemId: string) => void;
  isRepliesExpanded?: boolean;
}

/**
 * Format reply timestamp in a friendly way
 */
const formatReplyTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  // Less than 1 minute ago
  if (diffMins < 1) return 'just now';

  // Less than 60 minutes ago
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;

  // Less than 24 hours ago
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  // Today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (dateOnly.getTime() === today.getTime()) {
    return `at ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  // Yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateOnly.getTime() === yesterday.getTime()) {
    return `yesterday at ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  // Older - show date and time
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `on ${day}/${month}/${year} at ${time}`;
};

/**
 * Wrapper component that loads replies for a ProDoc item and handles reply creation
 */
const ProDocItemWithRepliesComponent: React.FC<ProDocItemWithRepliesProps> = ({
  item,
  children,
  inComponentView = false,
  agentPredominantColour,
  onCommentClick,
  isRepliesExpanded = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [replyCount, setReplyCount] = useState(0);
  const [hasLoadedCount, setHasLoadedCount] = useState(false);

  // Fetch reply count for this item
  const fetchReplyCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetProDocRepliesResponse>(
        `/pro-docs/items/${item.id}/replies`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.replies) {
        setReplyCount(response.data.replies.length);
        setHasLoadedCount(true);
      }
    } catch (err) {
      console.error('Error fetching reply count:', err);
    }
  }, [item.id, getAccessTokenSilently]);

  // Load reply count when component mounts if item has replies
  useEffect(() => {
    if (item.has_replies && !hasLoadedCount) {
      fetchReplyCount();
    }
  }, [item.has_replies, hasLoadedCount, fetchReplyCount]);

  return (
    <div
      className={`pro-doc-item-with-replies ${inComponentView ? 'in-component-view' : ''} ${isRepliesExpanded ? 'replies-expanded' : ''}`}
    >
      {/* Render the main item content */}
      {children}

      {/* Comment button with count - only show if not in component view */}
      {!inComponentView && onCommentClick && (
        <CommentCountButton
          count={replyCount}
          hasLoaded={hasLoadedCount}
          isExpanded={isRepliesExpanded}
          onClick={() => onCommentClick(item.id)}
        />
      )}
    </div>
  );
};

export default ProDocItemWithRepliesComponent;
